import { Injectable } from '@angular/core';
import { PendingEventLogRecord } from './front-event-log.types';

const DB_NAME = 'polpaico-event-logs';
const DB_VERSION = 1;
const STORE = 'pending-events';
const MAX_PENDING = 500;
const TTL_MS = 7 * 24 * 60 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class EventLogQueueStorage {
  private dbPromise: Promise<IDBDatabase>;

  constructor() {
    this.dbPromise = this.openDB();
  }

  async enqueue(record: PendingEventLogRecord): Promise<void> {
    await this.pruneExpired();
    const pending = await this.listPending();
    if (pending.length >= MAX_PENDING) {
      const oldest = pending.sort((a, b) => a.createdAt - b.createdAt)[0];
      if (oldest) {
        await this.remove(oldest.eventId);
      }
    }
    await this.put(record);
  }

  async listPending(): Promise<PendingEventLogRecord[]> {
    const db = await this.dbPromise;
    return new Promise((resolve, reject) => {
      const req = db.transaction(STORE, 'readonly').objectStore(STORE).getAll();
      req.onsuccess = () => resolve((req.result as PendingEventLogRecord[]) ?? []);
      req.onerror = () => reject(req.error);
    });
  }

  async remove(eventId: string): Promise<void> {
    const db = await this.dbPromise;
    return new Promise((resolve, reject) => {
      const req = db.transaction(STORE, 'readwrite').objectStore(STORE).delete(eventId);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }

  async update(record: PendingEventLogRecord): Promise<void> {
    await this.put(record);
  }

  private async put(record: PendingEventLogRecord): Promise<void> {
    const db = await this.dbPromise;
    return new Promise((resolve, reject) => {
      const req = db.transaction(STORE, 'readwrite').objectStore(STORE).put(record);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }

  private async pruneExpired(): Promise<void> {
    const now = Date.now();
    const pending = await this.listPending();
    for (const r of pending) {
      if (now - r.createdAt > TTL_MS) {
        await this.remove(r.eventId);
      }
    }
  }

  private openDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE, { keyPath: 'eventId' });
        }
      };
      req.onsuccess = (e) => resolve((e.target as IDBOpenDBRequest).result);
      req.onerror = (e) => reject((e.target as IDBOpenDBRequest).error);
    });
  }
}
